import { loadSuiteSettings } from "./settings";
import type { SuiteUser } from "./types";

const SUITE_ACCESS = "forgesuite.auth.access";
const SUITE_REFRESH = "forgesuite.auth.refresh";
const BF_ACCESS = "bookforge.auth.access";
const BF_REFRESH = "bookforge.auth.refresh";
const CF_ACCESS = "forge_suite_access";
const CF_REFRESH = "forge_suite_refresh";

export const PROXYPRO_URL =
  (import.meta.env.VITE_PROXYPRO_URL as string | undefined)?.replace(/\/$/, "") ||
  "https://proxy.vividlex.com";

const CLIENT = "musicforge";

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: SuiteUser;
}

export function authBaseUrl(): string {
  try {
    const s = loadSuiteSettings() as { authApiUrl?: string };
    const u = (s.authApiUrl || "").trim().replace(/\/$/, "");
    if (u && /proxy\.vividlex\.com/i.test(u)) return u;
  } catch {
    /* ignore */
  }
  return PROXYPRO_URL;
}

export function getAccessToken(): string | null {
  try {
    return (
      localStorage.getItem(SUITE_ACCESS) ||
      localStorage.getItem(BF_ACCESS) ||
      localStorage.getItem(CF_ACCESS) ||
      null
    );
  } catch {
    return null;
  }
}

export function isLoggedIn(): boolean {
  return Boolean(getAccessToken());
}

export function clearSession(): void {
  try {
    for (const k of [SUITE_ACCESS, SUITE_REFRESH, BF_ACCESS, BF_REFRESH, CF_ACCESS, CF_REFRESH]) {
      localStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
}

export function saveTokens(access: string, refresh?: string): void {
  const a = (access || "").trim();
  if (a.length < 20 || a.length > 8192) throw new Error("Invalid access token");
  try {
    localStorage.setItem(SUITE_ACCESS, a);
    localStorage.setItem(BF_ACCESS, a);
    localStorage.setItem(CF_ACCESS, a);
    if (refresh) {
      const r = refresh.trim();
      if (r.length >= 20 && r.length <= 8192) {
        localStorage.setItem(SUITE_REFRESH, r);
        localStorage.setItem(BF_REFRESH, r);
        localStorage.setItem(CF_REFRESH, r);
      }
    }
  } catch {
    throw new Error("Browser storage is blocked; can't keep you signed in.");
  }
}

export async function api<T = unknown>(
  path: string,
  opts: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(opts.headers);
  if (!headers.has("Content-Type") && opts.body) {
    headers.set("Content-Type", "application/json");
  }
  if (opts.auth !== false) {
    const tok = getAccessToken();
    if (tok) headers.set("Authorization", `Bearer ${tok}`);
  }
  let res: Response;
  try {
    res = await fetch(`${authBaseUrl()}${path}`, { ...opts, headers });
  } catch {
    throw new Error("Can't reach suite auth (proxy.vividlex.com).");
  }
  if (!res.ok) {
    const raw = await res.text().catch(() => "");
    const detail = extractApiError(raw, res.status);
    if (res.status === 401) {
      const sentAuth = opts.auth !== false && Boolean(getAccessToken());
      if (sentAuth) {
        clearSession();
        throw new Error(
          detail && !/^request failed/i.test(detail) ? detail : "Session expired. Please sign in again.",
        );
      }
      throw new Error(
        detail && !/^request failed/i.test(detail) ? detail : "Incorrect email or password",
      );
    }
    throw new Error(detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

function extractApiError(raw: string, status: number): string {
  const fallback = `Request failed (${status})`;
  if (!raw?.trim()) return fallback;
  try {
    const j = JSON.parse(raw) as {
      error?: string | { message?: string };
      detail?: string | { message?: string };
      message?: string;
    };
    if (j.error && typeof j.error === "object" && j.error.message) return String(j.error.message);
    if (typeof j.error === "string" && j.error.trim()) return j.error;
    if (j.detail && typeof j.detail === "object" && j.detail.message) return String(j.detail.message);
    if (typeof j.detail === "string" && j.detail.trim()) return j.detail;
    if (typeof j.message === "string" && j.message.trim()) return j.message;
  } catch {
    /* raw below */
  }
  return raw.length > 280 ? `${raw.slice(0, 280)}…` : raw;
}

export async function login(opts: { email: string; password: string }): Promise<TokenResponse> {
  const email = (opts.email || "").trim();
  if (!email || !email.includes("@")) throw new Error("Enter a valid email address");
  if (!(opts.password || "").length) throw new Error("Enter your password");
  const data = await api<TokenResponse>("/api/auth/login", {
    method: "POST",
    auth: false,
    body: JSON.stringify({ email, password: opts.password, client: CLIENT }),
  });
  if (!data?.access_token) throw new Error("Login response missing access token");
  saveTokens(data.access_token, data.refresh_token);
  return data;
}

export async function register(opts: {
  email: string;
  password: string;
  fullName?: string;
}): Promise<TokenResponse> {
  const email = (opts.email || "").trim();
  if (!email || !email.includes("@")) throw new Error("Enter a valid email address");
  if ((opts.password || "").length < 8) throw new Error("Password must be at least 8 characters");
  const data = await api<TokenResponse>("/api/auth/register", {
    method: "POST",
    auth: false,
    body: JSON.stringify({
      email,
      password: opts.password,
      full_name: opts.fullName || null,
      client: CLIENT,
    }),
  });
  if (!data?.access_token) throw new Error("Register response missing access token");
  saveTokens(data.access_token, data.refresh_token);
  return data;
}

export async function fetchMe(): Promise<SuiteUser> {
  return api("/api/auth/me");
}

export function startGoogleLoginWeb(): void {
  const origin = window.location.origin;
  const url = new URL(`${authBaseUrl()}/api/auth/google/start`);
  url.searchParams.set("return_origin", origin);
  url.searchParams.set("client", CLIENT);
  window.location.href = url.toString();
}

export function applyTokensFromHash(): boolean {
  try {
    const hash = window.location.hash || "";
    const path = window.location.pathname || "";
    if (!hash.includes("access_token") && !hash.includes("token=") && !path.includes("oauth")) {
      return false;
    }
    const raw = hash.replace(/^#/, "");
    const params = new URLSearchParams(
      raw.includes("access_token=") ? raw.slice(raw.indexOf("access_token=")) : raw,
    );
    const access = params.get("access_token") || params.get("token") || params.get("access");
    if (!access || access.length < 20) return false;
    saveTokens(access, params.get("refresh_token") || params.get("refresh") || undefined);
    history.replaceState(null, "", window.location.pathname + (window.location.search || "") || "/");
    return true;
  } catch {
    return false;
  }
}
